import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  Switch,
  Grid,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restaurant as MealIcon,
  LocalCafe as DrinkIcon,
  Stars as ComboIcon,
  CheckCircle as ActiveIcon,
  PauseCircleOutline as InactiveIcon,
  AutoAwesome as SparkleIcon,
} from "@mui/icons-material";
import plansApi, { Plan } from "../../api/plans";
import Layout from "../../components/layout/Layout";
import PlanModal from "./PlanModal";

const Plans: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await plansApi.getPlans({ includeInactive: true, all: true });
      setPlans(data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to fetch plans");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (plan: Plan) => {
    setTogglingId(plan._id);
    try {
      const updated = await plansApi.togglePlanActive(plan._id, plan.isActive);
      setPlans((prev) =>
        prev.map((p) => (p._id === plan._id ? { ...p, isActive: updated.isActive } : p))
      );
      setSuccessMsg(`Plan '${plan.title}' is now ${updated.isActive ? "LIVE in app" : "INACTIVE (draft)"}`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to update plan status");
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggleEnabled = async (plan: Plan) => {
    setTogglingId(plan._id);
    try {
      const updated = await plansApi.togglePlanEnabled(plan._id, plan.isEnabled);
      setPlans((prev) =>
        prev.map((p) => (p._id === plan._id ? { ...p, isEnabled: updated.isEnabled } : p))
      );
      setSuccessMsg(`Plan '${plan.title}' purchase is now ${updated.isEnabled ? "Enabled" : "Disabled"}`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to update plan purchase status");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeletePlan = async (id: string, title: string) => {
    if (!window.confirm(`Are you sure you want to permanently delete plan '${title}'? This cannot be undone.`)) {
      return;
    }
    try {
      await plansApi.deletePlan(id);
      setPlans((prev) => prev.filter((p) => p._id !== id));
      setSuccessMsg(`Plan '${title}' deleted successfully`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to delete plan");
    }
  };

  const handleEdit = (plan: Plan) => {
    setSelectedPlan(plan);
    setModalOpen(true);
  };

  const handleAddNew = () => {
    setSelectedPlan(null);
    setModalOpen(true);
  };

  // Filter and sort plans
  const filteredPlans = plans
    .filter((p) => {
      const matchesSearch =
        (p.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.description || "").toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType =
        selectedType === "all" ||
        (selectedType === "meal" && (p.planType === "meal" || !p.planType)) ||
        p.planType === selectedType;

      const matchesStatus =
        selectedStatus === "all" ||
        (selectedStatus === "active" && p.isActive) ||
        (selectedStatus === "inactive" && !p.isActive);

      return matchesSearch && matchesType && matchesStatus;
    })
    .sort((a, b) => {
      const orderA = a.sortOrder ?? 0;
      const orderB = b.sortOrder ?? 0;
      if (orderA !== orderB) return orderA - orderB;
      return (a.price ?? 0) - (b.price ?? 0);
    });

  const activeCount = plans.filter((p) => p.isActive).length;
  const inactiveCount = plans.filter((p) => !p.isActive).length;
  const drinkCount = plans.filter((p) => p.planType === "drink").length;
  const comboCount = plans.filter((p) => p.planType === "meal_and_drink").length;

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ color: "#0f172a" }}>
              Plan Management
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Configure subscription tiers, price matrix, and manage live availability on the customer app.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchPlans}
              sx={{ textTransform: "none" }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              sx={{ textTransform: "none", bgcolor: "#f97316", "&:hover": { bgcolor: "#ea580c" } }}
            >
              Create New Plan
            </Button>
          </Box>
        </Box>

        {/* Notifications */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMsg && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
            {successMsg}
          </Alert>
        )}

        {/* Top KPI Summary */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Typography variant="caption" color="textSecondary" fontWeight={600}>
                TOTAL PLANS
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color: "#0f172a" }}>
                {plans.length}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                All catalog offerings
              </Typography>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Typography variant="caption" color="success.main" fontWeight={600}>
                ACTIVE (LIVE IN APP)
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color: "#16a34a" }}>
                {activeCount}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Visible to mobile customers
              </Typography>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Typography variant="caption" color="warning.main" fontWeight={600}>
                INACTIVE (DRAFT MODE)
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color: "#ea580c" }}>
                {inactiveCount}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Hidden until activated
              </Typography>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #e2e8f0" }}>
              <Typography variant="caption" color="primary.main" fontWeight={600}>
                DRINKS & COMBOS
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color: "#0284c7" }}>
                {drinkCount + comboCount}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {drinkCount} Sips · {comboCount} Combos
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Search & Filters */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: "1px solid #e2e8f0" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 5 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search plans by title or keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 6, sm: 3, md: 3.5 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Plan Type"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="meal">Meal Plans Only</MenuItem>
                <MenuItem value="drink">Drink Plans (Kinnow Sips)</MenuItem>
                <MenuItem value="meal_and_drink">Combo Plans (Kinnow Complete)</MenuItem>
              </TextField>
            </Grid>

            <Grid size={{ xs: 6, sm: 3, md: 3.5 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="App Visibility"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active Only (Live)</MenuItem>
                <MenuItem value="inactive">Inactive Only (Drafts)</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {/* Loading Indicator */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
            <CircularProgress color="primary" />
          </Box>
        )}

        {/* Empty State */}
        {!loading && filteredPlans.length === 0 && (
          <Paper sx={{ p: 6, textAlign: "center", borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Typography variant="h6" color="textSecondary" fontWeight={600}>
              No plans match your filters
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Try adjusting your search query or reset the filters.
            </Typography>
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => {
                setSearchTerm("");
                setSelectedType("all");
                setSelectedStatus("all");
              }}
            >
              Reset Filters
            </Button>
          </Paper>
        )}

        {/* Plans Grid */}
        {!loading && filteredPlans.length > 0 && (
          <Grid container spacing={3}>
            {filteredPlans.map((plan) => {
              const isDrink = plan.planType === "drink";
              const isCombo = plan.planType === "meal_and_drink";

              return (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={plan._id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 2,
                      border: plan.isActive ? "1px solid #cbd5e1" : "1px dashed #f59e0b",
                      bgcolor: plan.isActive ? "#ffffff" : "#fffbeb",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      },
                    }}
                  >
                    {/* Card Media / Image */}
                    {plan.image && (
                      <CardMedia
                        component="img"
                        height="160"
                        image={plan.image}
                        alt={plan.title}
                        sx={{ objectFit: "cover" }}
                      />
                    )}

                    <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                      {/* Top Badges & Switch */}
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {/* Plan Type Badge */}
                          {isDrink ? (
                            <Chip
                              icon={<DrinkIcon fontSize="small" />}
                              label="Kinnow Sips (Drink)"
                              size="small"
                              sx={{ bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 700 }}
                            />
                          ) : isCombo ? (
                            <Chip
                              icon={<ComboIcon fontSize="small" />}
                              label="Kinnow Complete (Combo)"
                              size="small"
                              sx={{ bgcolor: "#ffedd5", color: "#c2410c", fontWeight: 700 }}
                            />
                          ) : (
                            <Chip
                              icon={<MealIcon fontSize="small" />}
                              label="Meal Plan"
                              size="small"
                              sx={{ bgcolor: "#dcfce7", color: "#15803d", fontWeight: 700 }}
                            />
                          )}

                          {plan.isBestseller && (
                            <Chip label="Bestseller" size="small" color="warning" sx={{ fontWeight: 700 }} />
                          )}

                          <Chip
                            label={`Order #${plan.sortOrder ?? 0}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 700, borderColor: "#cbd5e1", color: "#475569" }}
                          />
                        </Box>

                        {/* Live Status Switch */}
                        <Tooltip title={plan.isActive ? "Live in App (Click to deactivate)" : "Inactive in App (Click to make live)"}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              sx={{
                                mr: 0.5,
                                color: plan.isActive ? "#16a34a" : "#b45309",
                              }}
                            >
                              {plan.isActive ? "LIVE" : "DRAFT"}
                            </Typography>
                            <Switch
                              size="small"
                              checked={plan.isActive}
                              onChange={() => handleToggleActive(plan)}
                              disabled={togglingId === plan._id}
                              color="success"
                            />
                          </Box>
                        </Tooltip>
                      </Box>

                      {/* Teaser Badge */}
                      {plan.badgeText && (
                        <Box sx={{ mb: 1 }}>
                          <Chip
                            icon={<SparkleIcon sx={{ fontSize: "14px !important", color: "#ea580c !important" }} />}
                            label={plan.badgeText}
                            size="small"
                            sx={{
                              bgcolor: "#fff7ed",
                              color: "#c2410c",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              border: "1px solid #ffedd5",
                            }}
                          />
                        </Box>
                      )}

                      {/* Title & Description */}
                      <Typography variant="h6" fontWeight={700} sx={{ color: "#0f172a", mb: 0.5 }}>
                        {plan.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          mb: 2,
                          minHeight: 38,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {plan.description}
                      </Typography>

                      <Divider sx={{ my: 1.5 }} />

                      {/* Dynamic Pricing Matrix Summary */}
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
                        <Typography variant="caption" fontWeight={700} color="textSecondary" sx={{ display: "block", mb: 0.75 }}>
                          PRICING MATRIX (₹ / DAY)
                        </Typography>

                        {isDrink && (
                          <Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>
                                Coffee Explorer:
                              </Typography>
                              <Typography variant="body2" fontWeight={700} color="primary">
                                ₹{plan.drinkPricing?.coffee?.priceTrial || plan.price} (Trial) · ₹{plan.drinkPricing?.coffee?.priceWeekly || plan.priceWeekly} (W) · ₹{plan.drinkPricing?.coffee?.priceMonthly || plan.priceMonthly} (M)
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography variant="body2" fontWeight={600}>
                                Coffee & Blends:
                              </Typography>
                              <Typography variant="body2" fontWeight={700} color="primary">
                                ₹{plan.drinkPricing?.blend?.priceTrial || 200} (Trial) · ₹{plan.drinkPricing?.blend?.priceWeekly || 180} (W) · ₹{plan.drinkPricing?.blend?.priceMonthly || 160} (M)
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {isCombo && (
                          <Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>
                                Classic + Coffee:
                              </Typography>
                              <Typography variant="body2" fontWeight={700} color="primary">
                                ₹{plan.price} (Trial) · ₹{plan.priceWeekly} (W) · ₹{plan.priceMonthly} (M)
                              </Typography>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>
                                Light + Coffee:
                              </Typography>
                              <Typography variant="body2" fontWeight={700} color="primary">
                                ₹{plan.priceLight} (Trial) · ₹{plan.priceWeeklyLight} (W) · ₹{plan.priceMonthlyLight} (M)
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                              * Blends add +₹30/day upgrade delta across all durations
                            </Typography>
                          </Box>
                        )}

                        {!isDrink && !isCombo && (
                          <Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                              <Typography variant="body2" fontWeight={600}>
                                Classic Meal:
                              </Typography>
                              <Typography variant="body2" fontWeight={700} color="primary">
                                ₹{plan.price} (Trial) · ₹{plan.priceWeekly} (W) · ₹{plan.priceMonthly} (M)
                              </Typography>
                            </Box>
                            {plan.ifPortionsAvailable && (
                              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="body2" fontWeight={600}>
                                  Light Meal:
                                </Typography>
                                <Typography variant="body2" fontWeight={700} color="primary">
                                  ₹{plan.priceLight} (Trial) · ₹{plan.priceWeeklyLight} (W) · ₹{plan.priceMonthlyLight} (M)
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>

                      {/* Features Bullets */}
                      {Array.isArray(plan.features) && plan.features.length > 0 && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
                          {plan.features.slice(0, 3).map((feat, idx) => (
                            <Chip
                              key={idx}
                              label={feat}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.75rem" }}
                            />
                          ))}
                          {plan.features.length > 3 && (
                            <Chip
                              label={`+${plan.features.length - 3} more`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                            />
                          )}
                        </Box>
                      )}
                    </CardContent>

                    {/* Card Actions */}
                    <Box
                      sx={{
                        p: 2,
                        pt: 0,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Tooltip title={plan.isEnabled ? "Plan is purchasable" : "Purchasing is paused"}>
                        <Button
                          size="small"
                          color={plan.isEnabled ? "success" : "inherit"}
                          onClick={() => handleToggleEnabled(plan)}
                          disabled={togglingId === plan._id}
                          sx={{ textTransform: "none", fontSize: "0.8rem" }}
                        >
                          {plan.isEnabled ? "Purchase: ON" : "Purchase: OFF"}
                        </Button>
                      </Tooltip>

                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={() => handleEdit(plan)}
                          sx={{ textTransform: "none" }}
                        >
                          Edit
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeletePlan(plan._id, plan.title)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* Plan Create / Edit Modal */}
      <PlanModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchPlans}
        plan={selectedPlan}
      />
    </Layout>
  );
};

export default Plans;
